import React, { useState, useMemo } from 'react';
import { formatCurrency, formatDate } from '../../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { CheckCircle, Search, Filter, X } from 'lucide-react';

export const ReportsPaymentsTab = ({ received }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [prFilterPeriod, setPrFilterPeriod] = useState('');
  const [prFilterTenant, setPrFilterTenant] = useState('all');
  const [prFilterType, setPrFilterType] = useState('all');
  const [prFilterMethod, setPrFilterMethod] = useState('all');
  const [prShowFilters, setPrShowFilters] = useState(false);

  const filteredReceivedPayments = useMemo(() => {
    let result = received || [];
    if (prFilterPeriod) {
      result = result.filter(p => p.period_month === prFilterPeriod);
    }
    if (prFilterTenant !== 'all') {
      result = result.filter(p => (p.tenant_name || p.tenant_id) === prFilterTenant);
    }
    if (prFilterType !== 'all') {
      result = result.filter(p => p.payment_type === prFilterType);
    }
    if (prFilterMethod !== 'all') {
      result = result.filter(p => p.payment_method === prFilterMethod);
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter(p =>
        (p.tenant_name || '').toLowerCase().includes(q) ||
        (p.receipt_number || '').toLowerCase().includes(q) ||
        (p.period_month || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [received, prFilterPeriod, prFilterTenant, prFilterType, prFilterMethod, searchTerm]);

  const uniqueReceivedTenants = useMemo(() => {
    const names = new Set((received || []).map(p => p.tenant_name || p.tenant_id).filter(Boolean));
    return [...names].sort();
  }, [received]);

  const prHasActiveFilters = prFilterPeriod || prFilterTenant !== 'all' || prFilterType !== 'all' || prFilterMethod !== 'all';
  const clearPrFilters = () => { setPrFilterPeriod(''); setPrFilterTenant('all'); setPrFilterType('all'); setPrFilterMethod('all'); };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                Payments Received
              </CardTitle>
              <CardDescription>Verified payments from tenants</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative w-full sm:w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search payments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="reports-payment-search"
                />
              </div>
              <Button
                variant={prShowFilters ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPrShowFilters(!prShowFilters)}
                data-testid="reports-payment-toggle-filters"
              >
                <Filter className="w-4 h-4 mr-1" />
                Filters
                {prHasActiveFilters && <Badge variant="secondary" className="ml-1.5 h-5 px-1.5 bg-red-100 text-red-700 text-xs">{[prFilterPeriod, prFilterTenant !== 'all', prFilterType !== 'all', prFilterMethod !== 'all'].filter(Boolean).length}</Badge>}
              </Button>
              {prHasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearPrFilters}>
                  <X className="w-4 h-4 mr-1" /> Clear
                </Button>
              )}
            </div>
          </div>
          {prShowFilters && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-muted/50 rounded-lg">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Period</Label>
                <Input
                  type="month"
                  value={prFilterPeriod}
                  onChange={(e) => setPrFilterPeriod(e.target.value)}
                  data-testid="reports-filter-period"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Tenant</Label>
                <Select value={prFilterTenant} onValueChange={setPrFilterTenant}>
                  <SelectTrigger data-testid="reports-filter-tenant">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tenants</SelectItem>
                    {uniqueReceivedTenants.map(name => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Type</Label>
                <Select value={prFilterType} onValueChange={setPrFilterType}>
                  <SelectTrigger data-testid="reports-filter-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="rent">Rent</SelectItem>
                    <SelectItem value="deposit">Deposit</SelectItem>
                    <SelectItem value="advance">Advance</SelectItem>
                    <SelectItem value="utility">Utility</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Payment Method</Label>
                <Select value={prFilterMethod} onValueChange={setPrFilterMethod}>
                  <SelectTrigger data-testid="reports-filter-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Methods</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="gcash">GCash</SelectItem>
                    <SelectItem value="maya">Maya</SelectItem>
                    <SelectItem value="bank">Bank</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Receipt #</th>
                <th>Tenant</th>
                <th>Amount</th>
                <th>Type</th>
                <th>Method</th>
                <th>Period</th>
                <th>Verified</th>
              </tr>
            </thead>
            <tbody>
              {filteredReceivedPayments.map((payment, idx) => (
                <tr key={payment.payment_id || payment.receipt_number || `payment-${idx}`}>
                  <td><code className="text-xs bg-muted px-2 py-1 rounded">{payment.receipt_number || '-'}</code></td>
                  <td className="font-medium">{payment.tenant_name || payment.tenant_id}</td>
                  <td className="font-semibold text-emerald-600">{formatCurrency(payment.amount)}</td>
                  <td><Badge variant="outline" className="capitalize">{payment.payment_type}</Badge></td>
                  <td className="capitalize">{payment.payment_method}</td>
                  <td>{payment.period_month}</td>
                  <td className="text-sm text-muted-foreground">{payment.verified_at ? formatDate(payment.verified_at) : '-'}</td>
                </tr>
              ))}
              {filteredReceivedPayments.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">
                  {prHasActiveFilters || searchTerm ? 'No payments match your filters' : 'No verified payments'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        {filteredReceivedPayments.length > 0 && (
          <div className="mt-3 pt-3 border-t flex items-center justify-between text-sm text-muted-foreground">
            <span>{filteredReceivedPayments.length} payment(s) shown</span>
            <span className="font-semibold text-emerald-600">
              Total: {formatCurrency(filteredReceivedPayments.reduce((sum, p) => sum + (p.amount || 0), 0))}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
